// Mix: Auto-Mix (14 Schritte, Mensch entscheidet) und Konsole. Gestaltung wie im SRS-Prototyp.
import { useState } from 'react';
import type { Project } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { Panel, toast } from '../components/ui.tsx';
import { togglePlay } from '../components/Transport.tsx';
import { STEPS, analyse, plan, rebalance, applySuggestions, type Suggestion, type Analysis } from '../automix/automix.ts';
import type { MixReport, Role } from '../automix/measure.ts';
import { BlindTest, MixReportPanel, RoleTable, loudnessGap, matched } from './MixCheck.tsx';
import { FxPrefsPanel, fxChoice, FX_TASKS } from './AutoMixPlugins.tsx';
import { loadPrefs, prefFor, type Task } from '../automix/pluginmap.ts';
import { useBridge } from './Dante.tsx';
import { Mixer } from './Mixer.tsx';

const ROLES: [string, string][] = [['eq', 'EQ'], ['comp', 'Dynamik'], ['deesser', 'De-Esser'], ['saturation', 'Sättigung'], ['reverb', 'Hall'], ['delay', 'Delay'], ['buscomp', 'Bus-Komp'], ['utility', 'Utility']];

interface AmState {
  list: Suggestion[]; step: number; baseline: Project; log: string[]; released: string | null; plugins: Record<string, string>;
  an: Analysis; roles: Record<string, Role>; now: MixReport | null; ref: { name: string; r: MixReport; buf: AudioBuffer } | null;
}
/** Schlüssel eines Vorschlags, damit Entschiedenes beim Neuberechnen erhalten bleibt */
const keyOf = (s: Suggestion) => `${s.trackId}|${s.step}|${s.kind}|${s.role}|${s.f ?? ''}`;
/** Offene Vorschläge neu berechnen (andere Spurrolle, Referenz), Entschiedenes übernehmen */
function replan(a: AmState, patch: Partial<AmState>): AmState {
  const n = { ...a, ...patch };
  const fresh = plan(n.baseline, n.an, { roles: n.roles, ref: n.ref?.r, now: n.now ?? undefined });
  const done = new Map(a.list.filter((s) => s.status !== 'pending').map((s) => [keyOf(s), s]));
  const list = fresh.map((s) => { const d = done.get(keyOf(s)); return d && (d.tRole === s.tRole || s.trackId === 'bus') ? { ...s, id: d.id, value: d.value, status: d.status } : s; });
  return { ...n, list };
}
let saved: AmState | null = null; // bleibt beim Wechsel der Ansicht erhalten

export function Mix() {
  const [mode, setMode] = useState<'auto' | 'console'>('auto');
  const [am, setAmRaw] = useState<AmState | null>(saved);
  const setAm = (f: (a: AmState) => AmState) => setAmRaw((a) => { const n = a ? f(a) : a; saved = n; return n; });
  const playing = useStudio((s) => s.transport.playing);
  const [ab, setAb] = useState(!engine.comparing);
  const [abBusy, setAbBusy] = useState(false);
  const project = useStudio((s) => s.project);
  /** B = Ausgangsstand, lautstärkegleich: sonst gewinnt im Vergleich einfach die lautere Fassung */
  async function toggleAb() {
    if (!am) return;
    const on = !ab;
    if (on) { setAb(true); engine.setCompare(null); toast('A: mit Auto-Mix-Vorschlägen'); return; }
    setAbBusy(true);
    try {
      const gap = await loudnessGap(project, am.baseline, am.an.win);
      engine.setCompare(matched(am.baseline, gap)); setAb(false);
      toast(`B: ohne Auto-Mix, lautstärkegleich (${gap > 0 ? '+' : ''}${(Math.round(gap * 10) / 10).toString().replace('.', ',')} dB ausgeglichen)`, 4500);
    } finally { setAbBusy(false); }
  }
  const decided = am ? STEPS.filter((_, i) => stepDone(am, i)).length : 0;

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 10, height: '100%', minHeight: 0 }}>
      <section className="panel"><div className="mixhead">
        <div className="seg">
          <button className={mode === 'auto' ? 'on' : ''} onClick={() => setMode('auto')}>Auto-Mix</button>
          <button className={mode === 'console' ? 'on' : ''} onClick={() => setMode('console')}>Mixer</button>
        </div>
        <span className="t2">{am ? `${decided} von 14 Schritten entschieden` : 'Noch nicht analysiert'}</span>
        <div className="row" style={{ marginLeft: 'auto', gap: 10 }}>
          {am && <>
            <span className="t2">Auto-Mix hörbar</span>
            <button className={`toggle ${ab ? 'on' : ''}`} disabled={abBusy} title="Vergleich lautstärkegleich: B wird auf die Lautheit von A angeglichen" onClick={() => void toggleAb()} />
            <b className="mono" style={{ fontSize: 12, color: ab ? 'var(--blue)' : 'var(--muted)', minWidth: 160 }}>{abBusy ? 'gleiche Lautheit an …' : ab ? 'A: mit Vorschlägen' : 'B: ohne, lautstärkegleich'}</b>
          </>}
          <button className="btn small primary" onClick={() => void togglePlay()}>{playing ? 'Stopp' : 'Abspielen'}</button>
        </div>
      </div></section>
      <div style={{ minHeight: 0, overflow: 'auto' }}>{mode === 'console' ? <Mixer embedded /> : <AutoMix am={am} setAm={setAm} init={(a) => { saved = a; setAmRaw(a); }} />}</div>
    </div>
  );
}

const stepDone = (am: AmState, i: number) => {
  if (i === 13) return !!am.released;
  return am.list.filter((s) => s.step === i).every((s) => s.status !== 'pending');
};

function AutoMix({ am, setAm, init }: { am: AmState | null; setAm: (f: (a: AmState) => AmState) => void; init: (a: AmState) => void }) {
  const project = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const user = useStudio((s) => s.user);
  const bridge = useBridge();
  const [busy, setBusy] = useState<string | false>(false);
  const [monitor, setMonitor] = useState(engine.monitor);

  const run = async () => {
    setBusy('Analysiere …');
    try {
      const an = await analyse(project, (t) => setBusy(t));
      const roles = am?.roles ?? {}, ref = am?.ref ?? null;
      const list = plan(project, an, { roles, ref: ref?.r });
      init({ list, step: 0, baseline: structuredClone(project), log: [], released: null, plugins: am?.plugins ?? {}, an, roles, now: null, ref });
      toast('Analyse fertig. Jeder Vorschlag wartet auf eure Entscheidung.');
    } catch (e) { toast(`Analyse fehlgeschlagen: ${(e as Error).message}`, 8000); }
    finally { setBusy(false); }
  };

  const stamp = () => new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const first = user.name.split(' ')[0];
  /** Entscheidung festhalten und sofort hörbar machen */
  const decide = (ids: string[], patch: (s: Suggestion) => Partial<Suggestion>, verb: string) => {
    if (!am || !ids.length) return;
    const changed = am.list.filter((s) => ids.includes(s.id)).map((s) => ({ ...s, ...patch(s) }));
    setAm((a) => ({ ...a, list: a.list.map((s) => changed.find((c) => c.id === s.id) ?? s), log: [...changed.map((c) => `${stamp()}  ${first} ${verb}: ${c.trackName}, ${c.label}${c.status === 'edited' ? ` (${c.value} ${c.unit})` : ''}`), ...a.log].slice(0, 120) }));
    const toApply = changed.filter((c) => c.status === 'accepted' || c.status === 'edited');
    if (toApply.length) { const fxc = fxChoice(); commit(`Auto-Mix: ${STEPS[toApply[0].step].t}`, (p) => applySuggestions(p, toApply, fxc)); }
  };

  if (!am) return (
    <Panel title="Auto-Mix" style={{ height: '100%' }}>
      <div className="body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, alignContent: 'start', maxWidth: 1100 }}>
        <div className="stack">
          <div className="h1">Billboard-Standard in 14 Schritten. Ihr entscheidet.</div>
          <div className="t2" style={{ fontSize: 14, lineHeight: 1.55 }}>Die Engine misst den Mix, sucht die repräsentative Stelle (Refrain-Marker oder lauteste Stelle), rendert dort jede Spur einzeln, misst Lautheit, Klang und Rolle und schlägt für jeden Schritt eines kommerziellen Mixes konkrete Werte vor, mit Sicherheit in Prozent. Ihr übernehmt, dreht nach oder verwerft. Was unsicher ist, steht rechts unter „Hier irrt die KI vielleicht“. Freigeben kann nur ein Mensch.</div>
          <div><button className="btn primary" disabled={!!busy} onClick={() => void run()}>{busy || 'Spuren analysieren'}</button></div>
        </div>
        <div className="card" style={{ padding: 0 }}>{STEPS.map((s, i) => <div key={i} className="amstep" style={{ cursor: 'default' }}><span className="n">{i + 1}</span><b>{s.t}</b><small>{s.d}</small></div>)}</div>
      </div>
    </Panel>
  );

  const step = am.step;
  const items = am.list.filter((s) => s.step === step);
  const unsure = am.list.filter((s) => s.conf < 0.7 && s.status === 'pending');
  const fmt = (s: Suggestion) => `${String(Math.round(s.value * 100) / 100).replace('.', ',')}${s.unit ? ` ${s.unit}` : ''}`;
  const prefs = loadPrefs();
  const pluginName = (s: Suggestion) => {
    if (s.trackId === 'bus') return 'SRS Standard';
    const task = s.role === 'utility' ? (s.kind === 'gain' ? 'gain' : '') : s.role;
    const id = task && FX_TASKS.includes(task as Task) ? prefFor(prefs, task as Task, s.tRole) : '';
    return id ? bridge.plugins.find((p) => p.id === id)?.name ?? 'eigenes Plugin' : 'SRS Standard';
  };

  function nudge(s: Suggestion, dir: number) {
    const v = Math.max(s.min, Math.min(s.max, Math.round((s.value + dir * s.stepSize) * 100) / 100));
    const decidedBefore = s.status === 'accepted' || s.status === 'edited';
    setAm((a) => ({ ...a, list: a.list.map((x) => (x.id === s.id ? { ...x, value: v, status: decidedBefore ? (v === x.ai ? 'accepted' : 'edited') : x.status } : x)) }));
    if (decidedBefore) { const fxc = fxChoice(); commit(`Auto-Mix: ${STEPS[s.step].t}`, (p) => applySuggestions(p, [{ ...s, value: v, status: 'edited' }], fxc), { merge: true }); }
  }

  return (
    <div className="am3">
      <Panel title="Mix-Ablauf" right={<span className="pill">Chart-Standard</span>}>
        <div style={{ overflow: 'auto', flex: 1 }}>
          {STEPS.map((s, i) => {
            const it = am.list.filter((x) => x.step === i);
            const acc = it.filter((x) => x.status === 'accepted' || x.status === 'edited').length;
            const low = it.some((x) => x.conf < 0.7 && x.status === 'pending');
            return (
              <button key={i} className={`amstep ${i === step ? 'on' : ''} ${stepDone(am, i) ? 'done' : ''}`} onClick={() => setAm((a) => ({ ...a, step: i }))}>
                <span className="n">{stepDone(am, i) ? '✓' : i + 1}</span><b>{s.t}</b>
                <small>{i === 13 ? (am.released ? 'freigegeben' : 'Mensch gibt frei') : it.length ? `${acc} von ${it.length} übernommen` : 'nichts zu tun'}</small>
                {low && <span className="pill amber badge">prüfen</span>}
              </button>
            );
          })}
        </div>
        <div className="row" style={{ padding: 12, borderTop: '1px solid var(--line-soft)' }}>
          <button className="btn small primary" onClick={() => decide(am.list.filter((s) => s.status === 'pending' && s.conf >= 0.85).map((s) => s.id), () => ({ status: 'accepted' }), 'angenommen')}>Sichere Vorschläge annehmen</button>
          <button className="btn small" disabled={!!busy} onClick={() => void run()}>{busy || 'Neu messen'}</button>
        </div>
      </Panel>

      <Panel title={<><span className="mono" style={{ color: 'var(--blue)', fontWeight: 500, fontSize: 13, letterSpacing: 0 }}>Schritt {step + 1} von 14</span>&nbsp; {STEPS[step].t}</>} right={step < 13 && <>
        <button className="btn small" onClick={() => decide(items.filter((s) => s.status === 'pending').map((s) => s.id), () => ({ status: 'accepted' }), 'angenommen')}>Alle annehmen</button>
        <button className="btn small" onClick={() => decide(items.filter((s) => s.status === 'pending').map((s) => s.id), () => ({ status: 'skipped' }), 'übersprungen')}>Schritt überspringen</button>
        <button className="btn small" onClick={() => setAm((a) => ({ ...a, step: Math.min(13, a.step + 1) }))}>Weiter</button>
      </>}>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <div className="t2" style={{ padding: '12px 16px' }}>{STEPS[step].d}</div>
          {step === 0 && <RoleTable project={project} an={am.an} roles={am.roles} onRole={(id, r) => {
            const roles = { ...am.roles }; if (r) roles[id] = r; else delete roles[id];
            setAm((a) => replan(a, { roles }));
          }} />}
          {step === 2 && <div className="card" style={{ margin: '0 16px 12px', display: 'flex', gap: 12, alignItems: 'center' }}>
            <div className="t2" style={{ fontSize: 12.5, flex: 1 }}>EQ, Kompressor und Sättigung verändern die Lautheit der Spuren. Nach den Schritten 4 bis 8 hier noch einmal nachmessen: die Fader werden dann so vorgeschlagen, dass jede Spur wieder auf ihrem Balance-Ziel liegt.</div>
            <button className="btn small" disabled={!!busy} onClick={async () => {
              setBusy('Balance wird nachgemessen …');
              try {
                const fresh = await rebalance(useStudio.getState().project, am.an, am.roles, (t) => setBusy(t));
                const ids = new Set(fresh.map((s) => s.trackId));
                setAm((a) => ({ ...a, list: [...a.list.filter((s) => !(s.step === 2 && s.kind === 'volume' && s.status === 'pending' && ids.has(s.trackId))), ...fresh] }));
                toast(fresh.length ? `${fresh.length} Fader-Vorschläge nach der Bearbeitung` : 'Balance liegt auf den Zielen (Abweichung unter 0,5 dB).');
              } finally { setBusy(false); }
            }}>{busy || 'Balance nachmessen'}</button>
          </div>}
          {step === 12 && <MixReportPanel an={am.an} now={am.now} setNow={(r) => setAm((a) => replan(a, { now: r }))} refr={am.ref} setRef={(ref) => setAm((a) => replan(a, { ref }))} />}
          {step === 13 ? (
            <div className="stack" style={{ padding: '0 16px', maxWidth: 640 }}>
              <div className="row">
                <button className={`btn ${monitor.phone ? 'on' : ''}`} onClick={() => { const m = { ...monitor, phone: !monitor.phone }; setMonitor(m); engine.setMonitor(m); }}>Handy-Lautsprecher</button>
                <button className={`btn ${monitor.mono ? 'on' : ''}`} onClick={() => { const m = { ...monitor, mono: !monitor.mono }; setMonitor(m); engine.setMonitor(m); }}>Mono</button>
                <button className="btn" onClick={() => void togglePlay()}>Abspielen</button>
              </div>
              <div className="card">Mit Handy und Mono abhören, dann auf Kopfhörern. Erst danach freigeben.</div>
              <BlindTest baseline={am.baseline} win={am.an.win} onDone={(line) => setAm((a) => ({ ...a, log: [`${stamp()}  ${line}`, ...a.log] }))} />
              {am.released ? <span className="pill green">Freigegeben: {am.released}</span> : (
                <div><button className="btn primary" onClick={() => {
                  if (!STEPS.slice(0, 13).every((_, i) => stepDone(am, i))) return toast('Noch nicht alle Schritte entschieden. Die offenen sind links ohne Haken.');
                  setAm((a) => ({ ...a, released: `${user.name}, ${new Date().toLocaleString('de-DE')}`, log: [`${stamp()}  ${first} hat den Mix freigegeben`, ...a.log] }));
                  toast('Mix freigegeben');
                }}>Mix freigeben</button></div>
              )}
            </div>
          ) : !items.length ? <div className="empty">In diesem Projekt gibt es hier nichts zu tun.</div> : (
            <table className="amtable">
              <thead><tr><th>Spur</th><th>Vorschlag</th><th>Plugin</th><th>Wert, anpassbar</th><th>Sicherheit</th><th /></tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className={s.conf < 0.7 ? 'low' : ''}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{s.trackName}</td>
                    <td>{s.label}{s.note && <div className="warn">KI unsicher: {s.note}</div>}</td>
                    <td className="plug">{ROLES.find((r) => r[0] === s.role)?.[1] ?? s.role}<b>{pluginName(s)}</b></td>
                    <td>
                      <div className="stepper">
                        <button onClick={() => nudge(s, -1)}>–</button>
                        <span className={s.value !== s.ai ? 'edited' : ''}>{fmt(s)}</span>
                        <button onClick={() => nudge(s, 1)}>+</button>
                      </div>
                    </td>
                    <td><div className="confbar"><small>KI {Math.round(s.conf * 100)} %</small><div><i style={{ width: `${s.conf * 100}%`, background: s.conf >= 0.8 ? 'var(--green)' : s.conf >= 0.65 ? 'var(--amber)' : 'var(--claim)' }} /></div></div></td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {s.status === 'pending' ? <>
                        <button className="btn small primary" onClick={() => decide([s.id], (x) => ({ status: x.value === x.ai ? 'accepted' : 'edited' }), 'angenommen')}>Annehmen</button>{' '}
                        <button className="btn small" onClick={() => decide([s.id], () => ({ status: 'skipped' }), 'abgelehnt')}>Nein</button>
                      </> : <>
                        <span className={`pill ${s.status === 'skipped' ? '' : 'green'}`}>{s.status === 'skipped' ? 'abgelehnt' : s.status === 'edited' ? 'angepasst' : 'übernommen'}</span>{' '}
                        <button className="btn small" title="Neu entscheiden" onClick={() => setAm((a) => ({ ...a, list: a.list.map((x) => (x.id === s.id ? { ...x, status: 'pending' } : x)) }))}>↺</button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      <div className="stack" style={{ minHeight: 0 }}>
        <Panel title="Hier irrt die KI vielleicht" right={<span className="pill amber">{unsure.length} offen</span>} style={{ maxHeight: '38%' }}>
          <div style={{ overflow: 'auto' }}>
            {unsure.length === 0 ? <div className="t2" style={{ padding: 16 }}>Keine unsicheren Vorschläge offen.</div> : unsure.map((s) => (
              <div key={s.id} className="irrt" onClick={() => setAm((a) => ({ ...a, step: s.step }))}>
                <b>{s.step + 1}. {s.trackName}: {s.label}</b>
                <p>{s.note ?? `Sicherheit nur ${Math.round(s.conf * 100)} %. Bitte im Kontext anhören.`}</p>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Eure Plugins pro Aufgabe" right={<span className="pill">{bridge.connected ? 'über die Bridge' : 'Bridge aus'}</span>}>
          <div style={{ overflow: 'auto', padding: '10px 14px 12px' }}><FxPrefsPanel /></div>
        </Panel>
        <Panel title="Protokoll" right={<span className="pill">wer hat was entschieden</span>} style={{ flex: 1 }}>
          <div className="log">{am.log.length ? am.log.map((l, i) => <div key={i}>{l}</div>) : <div className="muted">Noch keine Entscheidungen.</div>}</div>
        </Panel>
      </div>
    </div>
  );
}
